
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {SortDirection} from './SortDirection';
import {FieldModelSortDirection} from './SortDirection';

/**
 * SortingEnum struct
 */
class SortingEnum {
  /**
   * Initialize struct
   * @param {!SortDirection=} direction
   * @param {!Map=} enumMnemonicToUiName
   * @constructor
   */
  constructor (direction = new SortDirection(SortDirection.ASCENDING), enumMnemonicToUiName = new Map()) {
    this.Direction = direction
    this.EnumMnemonicToUiName = enumMnemonicToUiName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SortingEnum}
   * @param {!SortingEnum} other Other struct
   * @returns {!SortingEnum} This struct
   */
  copy (other) {
    if (other.Direction != null) {
      this.Direction = SortDirection.fromObject(other.Direction)
    } else {
      this.Direction = null
    }
    if (other.EnumMnemonicToUiName != null) {
      this.EnumMnemonicToUiName = new Map()
      Object.keys(other.EnumMnemonicToUiName).forEach(key => {
        if (key != null) {
          let tempKey
          tempKey = key
          if (other.EnumMnemonicToUiName[key] != null) {
            let tempValue
            tempValue = other.EnumMnemonicToUiName[key]
            this.EnumMnemonicToUiName.set(tempKey, tempValue)
          } else {
            this.EnumMnemonicToUiName.set(tempKey, null)
          }
        }
      })
    } else {
      this.EnumMnemonicToUiName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SortingEnum}
   * @returns {!SortingEnum} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SortingEnumModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SortingEnumModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SortingEnum}
   * @param {!SortingEnum} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SortingEnum)) {
      throw new TypeError('Instance of SortingEnum is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SortingEnum}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Direction: ((this.Direction != null) ? this.Direction : null),
      EnumMnemonicToUiName: ((this.EnumMnemonicToUiName != null) ? fbe.MapToObject(new Map(Array.from(this.EnumMnemonicToUiName, item => [((item[0] != null) ? item[0] : null), ((item[1] != null) ? item[1] : null)]))) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SortingEnum.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SortingEnum} other Object value
   * @returns {!SortingEnum} Created struct
   */
  static fromObject (other) {
    return new SortingEnum().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SortingEnum}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SortingEnum.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SortingEnum}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 141
  }
}

export { SortingEnum };

/**
 * Fast Binary Encoding SortingEnum field model
 */
class FieldModelSortingEnum extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Direction = new FieldModelSortDirection(buffer, 4 + 4)
    this.EnumMnemonicToUiName = new fbe.FieldModelMap(new fbe.FieldModelString(buffer, this.Direction.fbeOffset + this.Direction.fbeSize), new fbe.FieldModelString(buffer, this.Direction.fbeOffset + this.Direction.fbeSize), buffer, this.Direction.fbeOffset + this.Direction.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSortingEnum}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSortingEnum}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Direction.fbeSize + this.EnumMnemonicToUiName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSortingEnum}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Direction.fbeExtra + this.EnumMnemonicToUiName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSortingEnum}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSortingEnum.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSortingEnum}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 141
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSortingEnum}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSortingEnum}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Direction.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Direction.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Direction.fbeSize

    if ((fbeCurrentSize + this.EnumMnemonicToUiName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EnumMnemonicToUiName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EnumMnemonicToUiName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSortingEnum}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSortingEnum}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSortingEnum}
   * @param {!SortingEnum} fbeValue Default value, defaults is new SortingEnum()
   * @returns {!SortingEnum} SortingEnum value
   */
  get (fbeValue = new SortingEnum()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSortingEnum}
   * @param {!SortingEnum} fbeValue SortingEnum value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Direction.fbeSize) <= fbeStructSize) {
      fbeValue.Direction = this.Direction.get(new SortDirection(SortDirection.ASCENDING))
    } else {
      fbeValue.Direction = new SortDirection(SortDirection.ASCENDING)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Direction.fbeSize

    if ((fbeCurrentSize + this.EnumMnemonicToUiName.fbeSize) <= fbeStructSize) {
      this.EnumMnemonicToUiName.get(fbeValue.EnumMnemonicToUiName)
    } else {
      fbeValue.EnumMnemonicToUiName.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EnumMnemonicToUiName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSortingEnum}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSortingEnum}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSortingEnum}
   * @param {!SortingEnum} fbeValue SortingEnum value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSortingEnum}
   * @param {SortingEnum} fbeValue SortingEnum value
   */
  setFields (fbeValue) {
    this.Direction.set(fbeValue.Direction)
    this.EnumMnemonicToUiName.set(fbeValue.EnumMnemonicToUiName)
  }
}

export { FieldModelSortingEnum };

/**
 * Fast Binary Encoding SortingEnum model
 */
class SortingEnumModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSortingEnum(this.buffer, 4)
  }

  /**
   * Get the SortingEnum model
   * @this {!SortingEnumModel}
   * @returns {!FieldModelSortingEnum} model SortingEnum model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SortingEnumModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SortingEnumModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SortingEnumModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SortingEnumModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSortingEnum.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SortingEnumModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SortingEnumModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SortingEnumModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SortingEnumModel}
   * @param {!SortingEnum} value SortingEnum value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SortingEnumModel}
   * @param {!SortingEnum} value SortingEnum value, defaults is new SortingEnum()
   * @return {!object} Deserialized SortingEnum value and its size
   */
  deserialize (value = new SortingEnum()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SortingEnum(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SortingEnum(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SortingEnumModel}
   * @param {!number} prev Previous SortingEnum model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SortingEnumModel };
